import styles from './TravelReceiptsCardView.module.scss';

import { Checkbox, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TravelReceipts } from '@work4all/models/lib/Classes/TravelReceipts.entity';

import { formatNumberAsCurrency } from '@work4all/utils';
import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';

interface TravelReceiptsCardViewProps {
  data: TravelReceipts[];
  selectedRows: string[];
  onRowSelection: (id: string, selected: boolean) => void;
  onClick: (receipt: TravelReceipts) => void;
}

export const TravelReceiptsCardView: React.FC<TravelReceiptsCardViewProps> = ({
  data,
  selectedRows,
  onRowSelection,
  onClick,
}) => {
  const { t } = useTranslation();

  const formatDate = (date: string) => {
    return date
      ? DateTime.fromISO(date).toLocaleString(DateTimeCustom.DATE_SIMPLE_2YEAR)
      : '';
  };

  return (
    <div className={styles.container}>
      {data.map((receipt) => {
        const isSelected = selectedRows.includes(String(receipt.id));

        return (
          <div
            key={receipt.id}
            className={styles.receiptItem}
            onClick={() => onClick(receipt)}
          >
            <div className={styles.receiptContent}>
              <div className={styles.header}>
                <Checkbox
                  checked={isSelected}
                  onChange={(e) =>
                    onRowSelection(String(receipt.id), e.target.checked)
                  }
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  className={styles.title}
                >
                  {receipt.receiptKind?.name || '-'}
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {formatNumberAsCurrency(receipt.expenditure || 0)}
                </Typography>
              </div>

              {receipt.description && (
                <Typography variant="body2" color="text.secondary">
                  {receipt.description}
                </Typography>
              )}

              <div className={styles.details}>
                <div className={styles.detailRow}>
                  <Typography variant="caption" color="text.secondary">
                    {t('COMMON.DOCUMENT_DATE')}:
                  </Typography>
                  <Typography variant="caption">
                    {formatDate(receipt.date)}
                  </Typography>
                </div>

                {receipt.receiptKindTravelCost?.name && (
                  <div className={styles.detailRow}>
                    <Typography variant="caption" color="text.secondary">
                      {t('COMMON.RECEIPT_KIND_TRAVEL_COSTS')}:
                    </Typography>
                    <Typography variant="caption">
                      {receipt.receiptKindTravelCost.name}
                    </Typography>
                  </div>
                )}

                {receipt.paymentMethod?.name && (
                  <div className={styles.detailRow}>
                    <Typography variant="caption" color="text.secondary">
                      {t('COMMON.PAYMENT_KIND')}:
                    </Typography>
                    <Typography variant="caption">
                      {receipt.paymentMethod.name}
                    </Typography>
                  </div>
                )}

                {receipt.travelExpenses?.user?.displayName && (
                  <div className={styles.detailRow}>
                    <Typography variant="caption" color="text.secondary">
                      {t('COMMON.CAPTURER')}:
                    </Typography>
                    <Typography variant="caption">
                      {receipt.travelExpenses.user.displayName}
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
